import { Component, Input } from '@angular/core';
import { Router } from '@angular/router';
import moment from 'moment';
import { catchError, map, Observable, of } from 'rxjs';
import * as XLSX from 'xlsx';
import { BookingStatusEnum } from '../../../../../models/booking.model';
import {
  SeminarAttendeeModel,
  SeminarDateSeriesDetailsModel,
} from '../../../../../models/seminar.model';
import { TableHeaderInterfaceModel } from '../../../../../models/table.model';
import { UserModel } from '../../../../../models/user.model';
import { SeminarService } from '../../../../../services/seminar.service';
import { getStatus } from '../../../../../utils/booking.utils';
import { getParticipantConditionLabel } from '../../../../../utils/seminar.utils';
import {
  getUserFullName,
  getUserInitials,
} from '../../../../../utils/user.utils';
import {
  Filter,
  TableColumn,
  TableHeaderButton,
} from '../../../../shared-components/custom-table/custom-table.component';

interface InitialModel {
  backgroundColor: string;
  color: string;
  text: string;
}

interface TableAttendeeModel {
  id: number;
  initials: InitialModel;
  avatar?: string;
  name: string;
  email: string;
  phone?: string;
  jobTitle?: string;
  bookingNr: string;
  bookingId: number;
  status: BookingStatusEnum;
}

@Component({
  selector: 'app-seminar-attendees',
  templateUrl: './seminar-attendees.component.html',
  styleUrl: './seminar-attendees.component.scss',
  standalone: false,
})
export class SeminarAttendeesComponent {
  public isLoading = true;
  public dataSource$: Observable<TableAttendeeModel[]>;
  public cardSlides: TableHeaderInterfaceModel[] = [];
  private attendees: SeminarAttendeeModel[];
  @Input() seminarDateSeries: SeminarDateSeriesDetailsModel;

  constructor(
    private seminarService: SeminarService,
    private router: Router
  ) {}

  public tableHeaderButton: TableHeaderButton = {
    label: 'Herunterladen',
    icon: 'fa-arrow-down-to-line',
    onClick: () => {
      this.downloadList();
    },
  };

  public columns: TableColumn[] = [
    {
      columnDef: 'name',
      header: 'Name',
      width: '25%',
      sortable: true,
      render: (row: TableAttendeeModel) => {
        if (!row.avatar) {
          return `<div class="user-table-container"><div class="avatar" style="background-color: ${row.initials.backgroundColor}; color: ${row.initials.color}" >${row.initials.text}</div><span>${row.name}</span></div>`;
        }
        // handle avatar
        return `<div class="user-table-container">
          <img class="avatar" src="${row.avatar}" alt="${row.name}" />
          <span>${row.name}</span>
        </div>`;
      },
      onCellClick: this.onCellClick.bind(this),
    },
    {
      columnDef: 'email',
      header: 'E-Mail',
      width: '20%',
      sortable: true,
      fontWeight: '400',
      onCellClick: this.onCellClick.bind(this),
    },
    {
      columnDef: 'jobTitle',
      header: 'Beruf',
      width: '20%',
      sortable: true,
      fontWeight: '400',
      onCellClick: this.onCellClick.bind(this),
    },
    {
      columnDef: 'phoneNumber',
      header: 'Telefon',
      width: '15%',
      fontWeight: '400',
      onCellClick: this.onCellClick.bind(this),
    },
    {
      columnDef: 'bookingNr',
      header: 'Buchungs ID',
      width: '15%',
      fontWeight: '400',
      onCellClick: this.onCellClick.bind(this),
    },
    {
      columnDef: 'status',
      header: 'Status',
      sortable: true,
      width: '15%',
      fontWeight: '400',
      filterValue: row => getStatus(row).status,
      render: row => {
        const statusClass = getStatus(row).class;

        return `<div class="${statusClass}">
        ${getStatus(row).status}
      </div>`;
      },
      onCellClick: this.onCellClick.bind(this),
    },
    {
      columnDef: 'actions',
      header: 'Aktionen',
      headerAlign: 'center',
      cellAlign: 'center',
      width: '10%',
      fontWeight: '400',
      render: () =>
        `<button class="table-action-button"><i class="fa-regular fa-chevron-down"></i></button>`,
      onCellClick: this.onCellClick.bind(this),
    },
  ];

  public ngOnInit() {
    this.getAttendees();
  }

  private getAttendees(): void {
    this.seminarService
      .getSeminarDateSeriesAttendees(this.seminarDateSeries.dateSeriesId)
      .pipe(
        map(attendees => {
          this.attendees = attendees;
          const mappedAttendees: TableAttendeeModel[] = attendees.map(
            attendee => this.mapAttendeeToTableAttendeeModel(attendee)
          );
          this.dataSource$ = of(mappedAttendees);
        }),
        catchError(error => {
          console.error('Error fetching attendees', error);
          return of([]);
        })
      )
      .subscribe({
        complete: () => {
          this.initHeader();
          this.isLoading = false;
        },
      });
  }

  private mapAttendeeToTableAttendeeModel(
    attendee: SeminarAttendeeModel
  ): TableAttendeeModel {
    const user: UserModel = {
      id: null,
      firstname: attendee.firstName,
      lastname: attendee.lastName,
      email: attendee.email,
    };
    const late =
      attendee.status === 'pending' &&
      moment(attendee.createdAt).isBefore(moment().subtract(14, 'days'));
    return {
      ...attendee,
      initials: getUserInitials(user),
      name: getUserFullName(user),
      status: late ? BookingStatusEnum.LATE : attendee.status,
    };
  }

  private initHeader() {
    this.cardSlides = [
      {
        title: 'Alle Teilnehmer',
        details: this.getUserCountByStatus()?.pipe(
          map(count => `${count} Teilnehmer`)
        ),
        titleIcon: 'fa-user',
        selected: true,
      },
      {
        title: 'Bezahlt',
        filterString: BookingStatusEnum.CONFIRMED,
        details: this.getUserCountByStatus(BookingStatusEnum.CONFIRMED)?.pipe(
          map(count => `${count} Teilnehmer`)
        ),
        color0: '#548235',
        color80: '#DDE6D7',
      },
      {
        title: 'Warteliste',
        filterString: BookingStatusEnum.WAITLIST,
        details: this.getUserCountByStatus(BookingStatusEnum.WAITLIST)?.pipe(
          map(count => `${count} Teilnehmer`)
        ),
        color0: '#C00000',
        color80: '#F2CCCC',
      },
      {
        title: 'Verspätet',
        filterString: BookingStatusEnum.LATE,
        details: this.getUserCountByStatus(BookingStatusEnum.LATE)?.pipe(
          map(count => `${count} Teilnehmer`)
        ),
        color0: '#BF9000',
        color80: '#F2E9CC',
      },
      {
        title: 'Ausstehend',
        filterString: BookingStatusEnum.PENDING,
        details: this.getUserCountByStatus(BookingStatusEnum.PENDING)?.pipe(
          map(count => `${count} Teilnehmer`)
        ),
      },
    ];
  }

  private getUserCountByStatus(status?: BookingStatusEnum): Observable<number> {
    return this.dataSource$?.pipe(
      map(attendees =>
        status
          ? attendees.filter(attendee => attendee.status === status)?.length
          : attendees?.length
      ),
      catchError(error => {
        console.error(
          'Error fetching user count by type from dataSource',
          error
        );
        return of(0);
      })
    );
  }

  public onCardClicked(event: TableHeaderInterfaceModel) {
    this.cardSlides = this.cardSlides.map(slide => ({
      ...slide,
      selected: slide.title === event.title,
    }));

    if (event.title === 'Alle Teilnehmer' || !event.filterString) {
      this.filterAttendeesByStatus();
    } else {
      this.filterAttendeesByStatus(event.filterString as BookingStatusEnum);
    }
  }

  private filterAttendeesByStatus(status?: BookingStatusEnum) {
    const attendees = [...this.attendees];
    const mappedAttendees: TableAttendeeModel[] = attendees.map(user =>
      this.mapAttendeeToTableAttendeeModel(user)
    );
    if (!status) {
      this.dataSource$ = of(mappedAttendees);
      return;
    }
    this.dataSource$ = of(
      mappedAttendees.filter(attendee => attendee.status === status)
    );
  }

  public onCellClick(row: TableAttendeeModel, column: any) {
    this.router.navigate([
      'administration',
      'bookings',
      btoa(String(row.bookingId)),
    ]);
  }

  private getExportPrice(price?: number | string): number | '' {
    if (price === null || price === undefined || price === '') {
      return '';
    }
    return Number(Number(price).toFixed(2));
  }

  private getPaymentMethodLabel(method?: string): string {
    switch (method) {
      case 'paypal':
        return 'PayPal';
      case 'invoice':
        return 'Überweisung';
      default:
        return method ?? '';
    }
  }

  private downloadList() {
    // Step 1: Define headers
    const headers = [
      'Vorname',
      'Nachname',
      'E-Mail',
      'Geburtsdatum',
      'Beruf',
      'Telefon',
      'Buchungs-ID',
      'Status',
      'Preisgruppe',
      'Gesamtbetrag',
      'Gutscheincodes',
      'Endpreis',
      'Zahlungsmethode',
    ];

    // Step 2: Map the data into the required structure
    const shownBookingTotals = new Set<number>();
    const rows = this.attendees.map(item => {
      const showBookingTotal = !shownBookingTotals.has(item.bookingId);
      shownBookingTotals.add(item.bookingId);

      return [
        item.firstName,
        item.lastName,
        item.email,
        item.birthdate.toLocaleDateString('de-DE', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }),
        item.jobTitle,
        item.phoneNumber,
        item.bookingNr,
        getStatus(item).status,
        getParticipantConditionLabel(item.condition ?? 'regular'),
        showBookingTotal ? this.getExportPrice(item.totalPrice) : '',
        showBookingTotal ? (item.couponCodes ?? '') : '',
        showBookingTotal ? this.getExportPrice(item.discountedPrice) : '',
        showBookingTotal ? this.getPaymentMethodLabel(item.paymentMethod) : '',
      ];
    });

    // Step 3: Create worksheet data with headers
    const wsData = [headers, ...rows];

    // Step 4: Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    const germanCurrencyFormat = '#,##0.00\\ "€"';
    const priceColumnIndexes = [9, 11];
    for (let rowIndex = 1; rowIndex < wsData.length; rowIndex++) {
      for (const colIndex of priceColumnIndexes) {
        const address = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
        const cell = ws[address];
        if (cell?.t === 'n') {
          cell.z = germanCurrencyFormat;
          delete cell.w;
          XLSX.utils.format_cell(cell);
        }
      }
    }

    this.setAutoColumnWidths(ws, wsData);

    // Step 5: Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Teilnehmer');

    // Step 6: Generate filename
    const fileName = `Teilnehmer-${this.seminarDateSeries.seminar.title}-${moment().format('YYYY-MM-DD-HH:mm')}.xlsx`;

    // Step 7: Write and download file
    XLSX.writeFile(wb, fileName);
  }

  private setAutoColumnWidths(
    ws: XLSX.WorkSheet,
    wsData: (string | number)[][]
  ): void {
    const columnCount = wsData[0]?.length ?? 0;
    ws['!cols'] = Array.from({ length: columnCount }, (_, colIndex) => {
      let maxWidth = 0;
      for (let rowIndex = 0; rowIndex < wsData.length; rowIndex++) {
        const address = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
        const cell = ws[address];
        const value =
          cell?.w ??
          (wsData[rowIndex][colIndex] != null
            ? String(wsData[rowIndex][colIndex])
            : '');
        maxWidth = Math.max(maxWidth, value.length);
      }
      return { wch: maxWidth + 2 };
    });
  }

  public filters: Filter[] = [
    {
      filteredColumn: 'status',
      type: 'multiselect',
      selectOptions: [
        {
          value: 'confirmed',
          label: 'Bezahlt',
        },
        {
          value: 'late',
          label: 'Verspätet',
        },
        {
          value: 'waitlist',
          label: 'Warteliste',
        },
        {
          value: 'pending',
          label: 'Ausstehend',
        },
        {
          value: 'cancelled',
          label: 'Storniert',
        },
      ],
      placeholder: 'Status auswählen...',
      label: 'Status',
    },
  ];
}
